import { type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS, type PracticalExamSheetStatus } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { formatDuration } from '@/lib/format-duration';
import { courseTotalActiveSeconds, formatCourseProgressPercent } from '@/lib/course-duration';
import { roleLabel } from '@/lib/role-display';
import { extractUserRoleSlugs, normalizeUserRoles } from '@/lib/user-roles';
import { parseCustomFieldMediaIds } from '@/lib/custom-field-display';
import { profileOriginBack, userReturnQuery } from '@/lib/user-detail-navigation';
import { UserShowMediaField } from './UserShowMediaField';
import {
  formatDateTimeEs,
  formatUserCustomFieldValue,
  isCustomFieldMediaType,
  isPersonalOrGeneralGroup,
  relativeTimeEs,
  userRoleIcon,
  userShowFieldSections,
  userStatusLabel,
} from './user-show-utils';

interface CustomFieldRow {
  id: string;
  name: string;
  slug: string;
  type: string;
  value: unknown;
  rawValue?: string | null;
  options?: Array<{ label: string; value: string }>;
  mediaFiles?: Array<{ id: string; title?: string | null; filename?: string | null }>;
}

interface ConnectionTimeCourseSummary {
  courseId: string;
  courseName: string;
  activeSeconds: number;
  academicHours: number;
  minCompliancePercent: number | null;
  progressPercent: number | null;
  meetsMinCompliance: boolean | null;
}

interface ConnectionTimeSummary {
  totalActiveSeconds: number;
  todayActiveSeconds: number;
  sessionCount: number;
  byCourse?: ConnectionTimeCourseSummary[];
}

interface UserContractRow {
  id: string;
  code: string;
  status: string;
  generatedAt: string;
  acceptedAt?: string | null;
  courseName: string;
  templateTitle: string;
}

interface PolicyAcceptanceRow {
  id: string;
  policyTitle: string;
  policyVersion: string;
  acceptedAt: string;
  source: string;
}

interface EnrolledCourseRow {
  id: string;
  name: string;
  progressPercent: number;
}

interface UserEnrolledCourses {
  courses: EnrolledCourseRow[];
}

interface PracticalExamSheetRow {
  id: string;
  examDate: string;
  plate: string;
  passed: boolean;
  status: PracticalExamSheetStatus;
}

interface UserDetail {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  phone?: string;
  roles?: string[] | unknown;
  status?: string;
  createdAt?: string;
  lastLoginAt?: string;
  avatarUrl?: string;
  emailVerifiedAt?: string | null;
  trashed?: boolean;
  personalCustomFields?: CustomFieldRow[];
  customFieldSections?: { groupName: string; fields: CustomFieldRow[] }[];
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  pending_acceptance: 'Pendiente',
  accepted: 'Aceptado',
  generated: 'Generado',
};

function formatExamDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
}

function practicalSheetStatus(sheet: PracticalExamSheetRow): { label: string; modifier: string } {
  if (sheet.status !== 'final') return { label: 'Borrador', modifier: 'lms-user-show-status--pending' };
  if (sheet.passed) return { label: 'Aprobado', modifier: 'lms-user-show-status--accepted' };
  return { label: 'Reprobado', modifier: 'lms-user-show-status--failed' };
}

function contractStatusModifier(status: string): string {
  if (status === 'accepted') return 'lms-user-show-status--accepted';
  if (status === 'pending_acceptance') return 'lms-user-show-status--pending';
  return 'lms-user-show-status--default';
}

function policyAcceptanceSourceLabel(source: string): string {
  switch (source) {
    case 'contract_signing':
      return 'Firma de contrato';
    case 'register':
      return 'Registro';
    default:
      return 'Aceptación directa';
  }
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase();
}

function foldName(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function groupIcon(groupName: string) {
  const name = foldName(groupName);
  if (name.includes('comercial') || name.includes('pago') || name.includes('caja')) return 'payments';
  if (name.includes('academ')) return 'school';
  if (isPersonalOrGeneralGroup(groupName)) return 'person';
  return 'folder';
}

function isCommercialOrPayment(groupName: string, field: CustomFieldRow) {
  const key = foldName(`${groupName} ${field.name} ${field.slug}`);
  return key.includes('comercial') || key.includes('pago') || key.includes('caja') || field.type.toLowerCase() === 'peso';
}

function isWideField(field: CustomFieldRow) {
  const type = field.type.toLowerCase();
  const key = foldName(`${field.slug} ${field.name}`);
  return type === 'textarea' || key.includes('direccion') || key.includes('address') || key.includes('observ');
}

function isSelectLike(type: string) {
  const normalized = type.toLowerCase();
  return normalized === 'select' || normalized === 'radio';
}

function fieldValueClass(field: CustomFieldRow, empty: boolean) {
  if (empty) return 'lms-user-show-field__value lms-user-show-field__value--empty';
  const type = field.type.toLowerCase();
  if (type === 'date' || type === 'datetime') return 'lms-user-show-field__value lms-user-show-field__value--datetime';
  if (isSelectLike(type)) return 'lms-user-show-field__value lms-user-show-field__value--select';
  return 'lms-user-show-field__value';
}

function rolePillClass(role: string) {
  if (role === 'super-admin' || role === 'admin') return 'lms-user-show-pill lms-user-show-pill--role lms-user-show-pill--role-admin';
  if (role === 'teacher') return 'lms-user-show-pill lms-user-show-pill--role lms-user-show-pill--role-teacher';
  return 'lms-user-show-pill lms-user-show-pill--role';
}

function statusPillClass(status?: string) {
  if (status === 'inactive') return 'lms-user-show-pill lms-user-show-pill--inactive';
  if (status === 'deleted') return 'lms-user-show-pill lms-user-show-pill--deleted';
  return 'lms-user-show-pill lms-user-show-pill--active';
}

function UserShowCard({
  icon,
  title,
  hint,
  count,
  children,
  flush,
}: {
  icon: string;
  title: string;
  hint?: string | null;
  count?: string;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="lms-user-show-card">
      <div className="lms-user-show-card__head">
        <div className="lms-user-show-card__head-title">
          <span className="material-symbols-outlined" aria-hidden="true">
            {icon}
          </span>
          <h3 className="lms-user-show-card__title">{title}</h3>
        </div>
        {count ? <span className="lms-user-show-card__count">{count}</span> : null}
        {!count && hint ? (
          <span className={`lms-user-show-card__hint${hint === 'Datos registrados en matrícula' ? ' lms-user-show-card__hint--muted' : ''}`}>
            {hint}
          </span>
        ) : null}
      </div>
      <div className={flush ? 'lms-user-show-card__body lms-user-show-card__body--flush' : 'lms-user-show-card__body'}>
        {children}
      </div>
    </section>
  );
}

function CustomFieldValue({ field }: { field: CustomFieldRow }) {
  const rendered = formatUserCustomFieldValue(field);
  const empty = rendered === '—';
  const type = field.type.toLowerCase();
  return (
    <span className={fieldValueClass(field, empty)}>
      {!empty && (type === 'date' || type === 'datetime') ? (
        <span className="material-symbols-outlined" aria-hidden="true">
          calendar_month
        </span>
      ) : null}
      {!empty && isSelectLike(type) ? (
        <span className="material-symbols-outlined" aria-hidden="true">
          badge
        </span>
      ) : null}
      {rendered}
    </span>
  );
}

function fieldHasMediaFiles(field: CustomFieldRow) {
  return isCustomFieldMediaType(field.type) && parseCustomFieldMediaIds(field.value ?? field.rawValue).length > 0;
}

function FieldGrid({ fields, columns }: { fields: CustomFieldRow[]; columns: 2 | 3 }) {
  const textFields = fields.filter((field) => !fieldHasMediaFiles(field));
  const mediaFields = fields.filter((field) => fieldHasMediaFiles(field));

  return (
    <>
      {textFields.length > 0 ? (
        <div className={`lms-user-show-fields lms-user-show-fields--${columns}`}>
          {textFields.map((field) => (
            <div
              key={field.id}
              className={`lms-user-show-field${isWideField(field) ? ' lms-user-show-field--wide' : ''}${
                columns === 3 && isSelectLike(field.type) ? ' lms-user-show-field--span-2' : ''
              }`}
            >
              <span className="lms-user-show-field__label">{field.name}</span>
              <CustomFieldValue field={field} />
            </div>
          ))}
        </div>
      ) : null}
      {mediaFields.map((field) => (
        <UserShowMediaField key={field.id} field={field} />
      ))}
    </>
  );
}

export function UserShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission, canManageUser } = usePermissions();
  const canListUsers = hasPermission(PERMISSIONS.USUARIOS_VER);
  const canViewConnectionDetail =
    hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR) || hasPermission(PERMISSIONS.PROGRESO_TIEMPO_VER);
  const canViewContracts = hasPermission(PERMISSIONS.CONTRATOS_VER);
  const canViewPolicies = hasPermission(PERMISSIONS.POLITICAS_LEGALES_VER);
  const canViewProgress = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR) || hasPermission(PERMISSIONS.PROGRESO_VER);
  const canViewPracticalExams = hasPermission(PERMISSIONS.EVALUACIONES_VER);
  const back = profileOriginBack(searchParams, canListUsers);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', id],
    queryFn: async () => {
      const user = await api<UserDetail>(`/users/${id}`);
      return normalizeUserRoles(user);
    },
    enabled: Boolean(id),
  });

  const canEdit =
    hasPermission(PERMISSIONS.USUARIOS_EDITAR) &&
    Boolean(data) &&
    canManageUser(extractUserRoleSlugs(data!.roles));

  const { data: connectionTime, isLoading: isConnectionTimeLoading } = useQuery({
    queryKey: ['users', id, 'connection-time'],
    queryFn: () => api<ConnectionTimeSummary>(`/users/${id}/connection-time`),
    enabled: Boolean(id),
  });

  const { data: contractsData, isLoading: isContractsLoading } = useQuery({
    queryKey: ['users', id, 'contracts'],
    queryFn: () =>
      listResource<UserContractRow>('/contracts', {
        page: 1,
        perPage: 100,
        userId: id!,
      }),
    enabled: Boolean(id) && canViewContracts,
  });

  const { data: policyAcceptancesData, isLoading: isPoliciesLoading } = useQuery({
    queryKey: ['users', id, 'policy-acceptances'],
    queryFn: () =>
      api<{ data: PolicyAcceptanceRow[] }>(`/legal/policies/acceptances?userId=${id}&page=1&perPage=100`),
    enabled: Boolean(id) && canViewPolicies,
  });

  const { data: practicalExamsData, isLoading: isPracticalExamsLoading } = useQuery({
    queryKey: ['users', id, 'practical-exams'],
    queryFn: () =>
      listResource<PracticalExamSheetRow>('/practical-exams', {
        page: 1,
        perPage: 20,
        studentUserId: id!,
      }),
    enabled: Boolean(id) && canViewPracticalExams,
  });

  const { data: enrolledCoursesData, isLoading: isEnrolledCoursesLoading } = useQuery({
    queryKey: ['users', id, 'enrolled-courses'],
    queryFn: () => api<UserEnrolledCourses>(`/users/${id}/enrolled-courses`),
    enabled: Boolean(id),
  });

  const displayName = data?.fullName ?? data?.name ?? 'Usuario';
  const roleSlugs = extractUserRoleSlugs(data?.roles);
  const fieldSections = userShowFieldSections(
    data?.personalCustomFields ?? [],
    data?.customFieldSections ?? [],
    data?.phone,
  )
    .map((section) =>
      canListUsers
        ? section
        : {
            ...section,
            fields: section.fields.filter(
              (field) => !isCustomFieldMediaType(field.type) && !isCommercialOrPayment(section.groupName, field),
            ),
          },
    )
    .filter((section) => section.fields.length > 0);
  const contracts = contractsData?.data ?? [];
  const practicalExams = practicalExamsData?.data ?? [];
  const policyAcceptances = policyAcceptancesData?.data ?? [];
  const showContractsAndPolicies = canViewContracts || canViewPolicies;
  const isContractsAndPoliciesLoading =
    (canViewContracts && isContractsLoading) || (canViewPolicies && isPoliciesLoading);
  const enrolledCourses = enrolledCoursesData?.courses ?? [];
  const connectionCourses = connectionTime?.byCourse ?? [];
  const singleConnectionCourse = connectionCourses.length === 1 ? connectionCourses[0] : null;
  const totalActiveSeconds = connectionTime?.totalActiveSeconds ?? 0;
  const singleCourseTotalSeconds = singleConnectionCourse
    ? courseTotalActiveSeconds(singleConnectionCourse.academicHours)
    : null;
  const singleCourseProgressLabel =
    singleCourseTotalSeconds != null
      ? formatCourseProgressPercent(totalActiveSeconds, singleCourseTotalSeconds)
      : null;
  const courseCountLabel =
    enrolledCourses.length === 1 ? '1 curso' : `${enrolledCourses.length} cursos`;

  return (
    <div className="lms-site-admin-hub-page lms-user-show font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          {back ? (
            <HubBackLink to={back.to} label={back.label} />
          ) : (
            <HubBackLink label="Volver" onBack={() => navigate(-1)} />
          )}
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Detalles del Usuario</h1>
        </div>
        {canEdit && id ? (
          <Link to={`/usuarios/${id}/edit`} className="lms-user-show__edit">
            <span className="material-symbols-outlined" aria-hidden="true">
              edit
            </span>
            Editar Perfil
          </Link>
        ) : null}
      </div>

      {error ? (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="lms-user-show-loader">
          <Loader />
        </div>
      ) : (
        <>
          <div className="lms-user-show__profile">
            <div className="lms-user-show__identity">
              {data?.avatarUrl ? (
                <img src={data.avatarUrl} alt="" className="lms-user-show__avatar-img" />
              ) : (
                <div className="lms-user-show__avatar">{initials(displayName)}</div>
              )}
              <div>
                <div className="lms-user-show__name-row">
                  <h2 className="lms-user-show__name">{displayName}</h2>
                  <span className={statusPillClass(data?.trashed ? 'deleted' : data?.status)}>
                    <span className="lms-user-show-pill__dot" aria-hidden="true" />
                    {data?.trashed ? 'Eliminado' : userStatusLabel(data?.status)}
                  </span>
                  {roleSlugs.map((role) => (
                    <span key={role} className={rolePillClass(role)}>
                      <span className="material-symbols-outlined" aria-hidden="true">
                        {userRoleIcon(role)}
                      </span>
                      {roleLabel(role)}
                    </span>
                  ))}
                </div>
                <p className="lms-user-show__email">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    mail
                  </span>
                  {data?.email}
                </p>
              </div>
            </div>

            <div className="lms-user-show__kpi">
              <div className="lms-user-show__kpi-icon">
                <span className="material-symbols-outlined" aria-hidden="true">
                  timer
                </span>
              </div>
              <div>
                <div className="lms-user-show__kpi-label">Horas de conexión</div>
                {isConnectionTimeLoading ? (
                  <Loader />
                ) : (
                  <div className="lms-user-show__kpi-value">
                    {formatDuration(totalActiveSeconds)}
                    {singleCourseProgressLabel ? (
                      <span className="lms-user-show__kpi-pct">/ {singleCourseProgressLabel}</span>
                    ) : null}
                  </div>
                )}
              </div>
              {canViewConnectionDetail && id ? (
                <Link to={`/progreso/tiempo-conexion/${id}${userReturnQuery(id)}`} className="lms-user-show__kpi-link">
                  Ver detalle de sesiones
                  <span className="material-symbols-outlined" aria-hidden="true">
                    arrow_forward
                  </span>
                </Link>
              ) : null}
            </div>
          </div>

          <div className="lms-user-show__grid">
            <div className="lms-user-show__col">
              {fieldSections.map((section, index) => (
                <UserShowCard
                  key={`${section.groupName}-${index}`}
                  icon={groupIcon(section.groupName)}
                  title={section.groupName}
                >
                  <FieldGrid columns={3} fields={section.fields} />
                </UserShowCard>
              ))}

              {showContractsAndPolicies ? (
                <UserShowCard icon="gavel" title="Contrato y Políticas Legales">
                  {isContractsAndPoliciesLoading ? (
                    <div className="lms-user-show-loader">
                      <Loader />
                    </div>
                  ) : (
                    <>
                      {canViewContracts ? (
                        <div className="lms-user-show-block">
                          <div className="lms-user-show-block__label">Contratos</div>
                          {contracts.length === 0 ? (
                            <p className="lms-user-show-card__empty">Sin contratos generados.</p>
                          ) : (
                            <div className="space-y-3">
                              {contracts.map((contract) => (
                                <div key={contract.id} className="lms-user-show-row">
                                  <div className="lms-user-show-row__main">
                                    <div className="lms-user-show-row__icon">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        description
                                      </span>
                                    </div>
                                    <div>
                                      <p className="lms-user-show-row__title">
                                        {contract.templateTitle || contract.courseName}
                                      </p>
                                      <p className="lms-user-show-row__meta">{contract.code}</p>
                                    </div>
                                  </div>
                                  <div className="lms-user-show-row__actions">
                                    <span className={`lms-user-show-status ${contractStatusModifier(contract.status)}`}>
                                      <span className="lms-user-show-pill__dot" aria-hidden="true" />
                                      {CONTRACT_STATUS_LABELS[contract.status] ?? contract.status}
                                    </span>
                                    <Link to={`/contratos/${contract.id}${id ? userReturnQuery(id) : ''}`} className="lms-user-show-btn">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        visibility
                                      </span>
                                      Ver contrato
                                    </Link>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}

                      {canViewPolicies ? (
                        <div className="lms-user-show-block">
                          <div className="lms-user-show-block__label">Políticas legales</div>
                          {policyAcceptances.length === 0 ? (
                            <p className="lms-user-show-card__empty">Sin políticas aceptadas.</p>
                          ) : (
                            <div className="space-y-3">
                              {policyAcceptances.map((acceptance) => (
                                <div key={acceptance.id} className="lms-user-show-row lms-user-show-row--policy">
                                  <div className="lms-user-show-row__main">
                                    <div className="lms-user-show-row__icon lms-user-show-row__icon--muted">
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        policy
                                      </span>
                                    </div>
                                    <div>
                                      <p className="lms-user-show-row__title">{acceptance.policyTitle}</p>
                                      <p className="lms-user-show-row__meta lms-user-show-row__meta--plain">
                                        <span className="lms-user-show-row__source">
                                          {policyAcceptanceSourceLabel(acceptance.source)}
                                        </span>
                                        <span aria-hidden="true">•</span>
                                        <span>{formatDateTimeEs(acceptance.acceptedAt)}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <span className="lms-user-show-status lms-user-show-status--accepted">
                                    <span className="lms-user-show-pill__dot" aria-hidden="true" />
                                    Aceptado
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </UserShowCard>
              ) : null}
            </div>

            <div className="lms-user-show__col">
              <UserShowCard icon="auto_stories" title="Cursos Inscritos" count={isEnrolledCoursesLoading ? undefined : courseCountLabel}>
                {isEnrolledCoursesLoading ? (
                  <div className="lms-user-show-loader">
                    <Loader />
                  </div>
                ) : enrolledCourses.length === 0 ? (
                  <p className="lms-user-show-card__empty">Sin cursos inscritos.</p>
                ) : (
                  enrolledCourses.map((course) => {
                    const progress = Math.max(0, Math.min(100, course.progressPercent));
                    const nameClass = 'lms-user-show-course__name';
                    return (
                      <div key={course.id} className="lms-user-show-course">
                        <div className="lms-user-show-course__top">
                          {canViewProgress && id ? (
                            <Link to={`/progreso/${id}/${course.id}${userReturnQuery(id)}`} className={nameClass}>
                              {course.name}
                            </Link>
                          ) : (
                            <p className={nameClass}>{course.name}</p>
                          )}
                          <span className="lms-user-show-course__pct">{progress}%</span>
                        </div>
                        <p className="lms-user-show-course__desc">
                          Progreso de actividades y tiempo activo en plataforma.
                        </p>
                        <div className="lms-user-show-course__bar">
                          <div className="lms-user-show-course__bar-fill" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </UserShowCard>

              {canListUsers ? (
              <UserShowCard icon="settings_suggest" title="Información del Sistema">
                <div className="lms-user-show-system">
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Registrado</span>
                    <span className="lms-user-show-system__value">{formatDateTimeEs(data?.createdAt) ?? '—'}</span>
                  </div>
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Último acceso</span>
                    <div className="lms-user-show-system__value">
                      {data?.lastLoginAt ? (
                        <>
                          <div>{formatDateTimeEs(data.lastLoginAt)}</div>
                          <div className="lms-user-show-system__rel">{relativeTimeEs(data.lastLoginAt)}</div>
                        </>
                      ) : (
                        'Nunca'
                      )}
                    </div>
                  </div>
                  <div className="lms-user-show-system__row">
                    <span className="lms-user-show-system__label">Email verificado</span>
                    {data?.emailVerifiedAt ? (
                      <span className="lms-user-show-verified">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          check_circle
                        </span>
                        {formatDateTimeEs(data.emailVerifiedAt)}
                      </span>
                    ) : (
                      <span className="lms-user-show-pending">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          pending
                        </span>
                        Pendiente
                      </span>
                    )}
                  </div>
                </div>
              </UserShowCard>
              ) : null}

              {canViewPracticalExams ? (
                <UserShowCard icon="fact_check" title="Ficha práctica">
                  {isPracticalExamsLoading ? (
                    <div className="lms-user-show-loader">
                      <Loader />
                    </div>
                  ) : practicalExams.length === 0 ? (
                    <p className="lms-user-show-card__empty">Sin ficha práctica.</p>
                  ) : (
                    <div className="space-y-3">
                      {practicalExams.map((sheet) => {
                        const status = practicalSheetStatus(sheet);
                        const plate = sheet.plate.trim();
                        return (
                          <div key={sheet.id} className="lms-user-show-row">
                            <div className="lms-user-show-row__main">
                              <div className="lms-user-show-row__icon">
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  assignment
                                </span>
                              </div>
                              <div>
                                <p className="lms-user-show-row__title">Examen práctico clase B</p>
                                <p className="lms-user-show-row__meta lms-user-show-row__meta--plain">
                                  <span>{formatExamDate(sheet.examDate)}</span>
                                  {plate ? (
                                    <>
                                      <span aria-hidden="true">•</span>
                                      <span>{plate}</span>
                                    </>
                                  ) : null}
                                </p>
                              </div>
                            </div>
                            <div className="lms-user-show-row__actions">
                              <span className={`lms-user-show-status ${status.modifier}`}>
                                <span className="lms-user-show-pill__dot" aria-hidden="true" />
                                {status.label}
                              </span>
                              <Link
                                to={`/examen-practico/${sheet.id}${id ? userReturnQuery(id) : ''}`}
                                className="lms-user-show-btn"
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                                Ver ficha
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </UserShowCard>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
